import React, { useState, useMemo } from 'react';
import { Search, Trophy } from 'lucide-react';
import FilterModalWrapper from './FilterModalWrapper';

/**
 * ConferenceModal - Desktop modal for selecting NCAAF conferences
 * Provides searchable conference list
 * Adapted from mobile ConferenceSheet
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {string|Object} selectedConference - Currently selected conference
 * @param {Function} onConferenceSelect - Callback when conference is selected
 */
const ConferenceModal = ({
  isOpen,
  onClose,
  selectedConference,
  onConferenceSelect
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // NCAAF conferences
  const conferences = useMemo(() => [
    { id: 'SEC', name: 'SEC', fullName: 'Southeastern Conference' },
    { id: 'Big Ten', name: 'Big Ten', fullName: 'Big Ten Conference' },
    { id: 'ACC', name: 'ACC', fullName: 'Atlantic Coast Conference' },
    { id: 'Big 12', name: 'Big 12', fullName: 'Big 12 Conference' },
    { id: 'Pac-12', name: 'Pac-12', fullName: 'Pac-12 Conference' },
    { id: 'American', name: 'American', fullName: 'American Athletic Conference' },
    { id: 'Mountain West', name: 'Mountain West', fullName: 'Mountain West Conference' },
    { id: 'Sun Belt', name: 'Sun Belt', fullName: 'Sun Belt Conference' },
    { id: 'MAC', name: 'MAC', fullName: 'Mid-American Conference' },
    { id: 'C-USA', name: 'C-USA', fullName: 'Conference USA' },
    { id: 'Independent', name: 'Independent', fullName: 'Independent' }
  ], []);

  // Filter conferences by search query
  const filteredConferences = useMemo(() => {
    if (!searchQuery) return conferences;

    const query = searchQuery.toLowerCase();
    return conferences.filter(conference => {
      const name = (conference.name || '').toLowerCase();
      const fullName = (conference.fullName || '').toLowerCase();
      const id = (conference.id || '').toLowerCase();
      return name.includes(query) || fullName.includes(query) || id.includes(query);
    });
  }, [conferences, searchQuery]);

  const handleConferenceSelect = (conference) => {
    onConferenceSelect(conference.name || conference.id);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    onConferenceSelect(null);
    setSearchQuery('');
  };

  // Get selected conference name
  const getSelectedName = () => {
    if (!selectedConference) return null;
    if (typeof selectedConference === 'string') return selectedConference;
    return selectedConference.name || selectedConference.id;
  };

  const selectedName = getSelectedName();

  return (
    <FilterModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Select Conference"
      icon={Trophy}
      maxWidth="md"
      maxHeight="70vh"
    >
      {/* Search Bar */}
      <div className="p-4 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conferences..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-emerald-500
                     border border-white/10"
          />
        </div>
      </div>

      {/* Conference List */}
      <div className="overflow-y-auto">
        {/* All Conferences Option */}
        <button
          onClick={handleClearSelection}
          className={`
            w-full flex items-center gap-3 px-4 py-3 transition-colors
            ${!selectedName
              ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
              : 'hover:bg-gray-800/50 text-white'
            }
          `}
        >
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
            <Trophy className="w-4 h-4 text-gray-400" />
          </div>
          <span className="font-medium">All Conferences</span>
          {!selectedName && (
            <div className="ml-auto w-2 h-2 rounded-full bg-emerald-400" />
          )}
        </button>

        {/* Conference List */}
        {filteredConferences.map((conference) => {
          const isSelected = selectedName === conference.name || selectedName === conference.id;

          return (
            <button
              key={conference.id}
              onClick={() => handleConferenceSelect(conference)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 transition-colors
                ${isSelected
                  ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
                  : 'hover:bg-gray-800/50 text-white'
                }
              `}
            >
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-amber-400" />
              </div>

              <div className="flex-1 text-left">
                <div className="font-medium">{conference.name}</div>
                <div className="text-xs text-gray-500">{conference.fullName}</div>
              </div>

              {isSelected && (
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
              )}
            </button>
          );
        })}

        {filteredConferences.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500">No conferences found</p>
          </div>
        )}
      </div>
    </FilterModalWrapper>
  );
};

export default ConferenceModal;
